/**
 * Compress an image File using the browser Canvas API.
 *
 * @param {File} file         - Original image file
 * @param {object} [opts]
 * @param {number} [opts.maxWidth=1280]   - Max width in px (preserves aspect ratio)
 * @param {number} [opts.maxHeight=720]   - Max height in px (preserves aspect ratio)
 * @param {number} [opts.quality=0.85]    - JPEG/WebP quality 0–1
 * @param {string} [opts.outputType]      - Output MIME type (defaults to input type, falls back to image/jpeg)
 * @returns {Promise<File>} Compressed File with same name
 */
export async function compressImage(file, {
    maxWidth = 1280,
    maxHeight = 720,
    quality = 0.85,
    outputType,
} = {}) {
    const mimeType = outputType || (file.type === 'image/webp' ? 'image/webp' : 'image/jpeg');

    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { width, height } = img;

            // Scale down proportionally if larger than max dimensions
            const widthRatio = width > maxWidth ? maxWidth / width : 1;
            const heightRatio = height > maxHeight ? maxHeight / height : 1;
            const scale = Math.min(widthRatio, heightRatio);

            width = Math.round(width * scale);
            height = Math.round(height * scale);

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        // Canvas compression failed — return original
                        resolve(file);
                        return;
                    }
                    // Only use compressed version if it's actually smaller
                    if (blob.size >= file.size) {
                        resolve(file);
                        return;
                    }
                    const compressed = new File([blob], file.name, { type: mimeType, lastModified: Date.now() });
                    resolve(compressed);
                },
                mimeType,
                quality,
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            // On error return original so upload still works
            resolve(file);
        };

        img.src = objectUrl;
    });
}
